import React from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "../../lib/theme-context";
import { cn } from "../../lib/utils";

/**
 * Tactile theme toggle. Uses Lucide icons only.
 * Variants:
 *  - "icon"  : square 36/40px icon-only button (for topbar / floating UI).
 *  - "row"   : full-width row used inside dropdown menus / settings.
 */
export default function ThemeToggle({ variant = "icon", className }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const label = isDark ? "Hellen Modus aktivieren" : "Dunklen Modus aktivieren";

  if (variant === "row") {
    return (
      <button
        type="button"
        data-testid="theme-toggle"
        onClick={toggleTheme}
        className={cn(
          "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted",
          className,
        )}
        aria-label={label}
      >
        <span className="flex items-center gap-2">
          {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          <span>{isDark ? "Dark Mode" : "Light Mode"}</span>
        </span>
        <span className="text-xs text-muted-foreground">
          {isDark ? "→ Light" : "→ Dark"}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={toggleTheme}
      title={label}
      aria-label={label}
      className={cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-card text-foreground/80",
        "btn-tactile hover:text-foreground hover:border-border hover:bg-muted",
        "focus-visible:outline-none",
        className,
      )}
    >
      <span className="relative block h-4 w-4">
        <Sun
          className={cn(
            "absolute inset-0 h-4 w-4 transition-all duration-300",
            isDark ? "-rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100",
          )}
        />
        <Moon
          className={cn(
            "absolute inset-0 h-4 w-4 transition-all duration-300",
            isDark ? "rotate-0 scale-100 opacity-100" : "rotate-90 scale-0 opacity-0",
          )}
        />
      </span>
    </button>
  );
}
